import React, { useState, useRef, useEffect } from 'react';
import { userService } from '../../services/apiServices';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

const MfaActionButton = ({ user, onUpdate, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await userService.getMfaStatus(user._id);
      if (res.success) setMfaStatus(res.data);
    } catch {
      setMfaStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    const newValue = !mfaStatus?.requiredByAdmin;
    try {
      const res = await userService.toggleMfaRequired(user._id, newValue);
      if (res.success) {
        showSuccessToast(newValue ? 'MFA obligatoire activé' : 'MFA obligatoire désactivé');
        if (onUpdate) onUpdate();
        fetchStatus();
      }
    } catch (err) {
      showErrorToast(err.data?.message || 'Erreur');
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Réinitialiser MFA pour ${user.email} ? L'utilisateur devra reconfigurer MFA.`)) return;
    try {
      const res = await userService.resetMfa(user._id);
      if (res.success) {
        showSuccessToast('MFA réinitialisé');
        if (onUpdate) onUpdate();
        fetchStatus();
      }
    } catch (err) {
      showErrorToast(err.data?.message || 'Erreur');
    }
    setOpen(false);
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !mfaStatus) fetchStatus();
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={toggleOpen}
        className={compact
          ? `text-xs font-medium px-2 py-1 rounded transition-colors ${
              mfaStatus?.enabled
                ? 'text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`
          : `animated-button text-xs font-medium px-3 py-2 rounded-lg transition-all duration-300 flex items-center space-x-1 ${
              mfaStatus?.enabled
                ? 'text-emerald-600 hover:text-white hover:bg-emerald-600'
                : 'text-gray-500 hover:text-white hover:bg-gray-500'
            }`
        }
      >
        <i className={`fas ${mfaStatus?.enabled ? 'fa-shield-check' : 'fa-shield'}`}></i>
        <span>MFA</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64 rounded-xl border border-gray-200 bg-white shadow-xl p-3">
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
            </div>
          ) : mfaStatus ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut MFA</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  mfaStatus.enabled
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {mfaStatus.enabled ? 'Activé' : 'Désactivé'}
                </span>
              </div>

              {mfaStatus.enabled && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Appareils fiables</span>
                  <span className="text-xs font-medium text-gray-700">{mfaStatus.trustedDevicesCount || 0}</span>
                </div>
              )}

              <div className="border-t border-gray-100 pt-2 space-y-1">
                <button
                  onClick={handleToggle}
                  className="w-full text-left text-xs font-medium px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-700 flex items-center gap-2"
                >
                  <i className={`fas ${mfaStatus.requiredByAdmin ? 'fa-toggle-on text-indigo-600' : 'fa-toggle-off text-gray-400'}`}></i>
                  {mfaStatus.requiredByAdmin ? 'MFA obligatoire (actif)' : 'Rendre MFA obligatoire'}
                </button>

                {mfaStatus.enabled && (
                  <button
                    onClick={handleReset}
                    className="w-full text-left text-xs font-medium px-3 py-2 rounded-lg hover:bg-red-50 text-red-600 flex items-center gap-2"
                  >
                    <i className="fas fa-rotate-left"></i>
                    Réinitialiser MFA
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-2">Impossible de charger le statut</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MfaActionButton;
